/**
 * @fileoverview Job Integrations panel (MOR-555 Work OS Sync).
 *
 * Two distinct layers, kept visually separate:
 * 1. "Microsoft account" — the signed-in user's account-level provider
 *    connections (shared UI in -ui.microsoft-account-section.tsx, also used
 *    on Settings → Integrations).
 * 2. Per-provider job sync cards — bind a drive folder to THIS job
 *    (one connection per provider per job) and monitor sync runs.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useAction } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Input } from "@/ui/primitives/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/composed/overlays";
import { toast } from "sonner";
import {
  AlertTriangle,
  ChevronRight,
  Folder,
  FolderOpen,
  Globe,
  Library,
  Loader2,
  RefreshCw,
  Search,
  Unplug,
} from "lucide-react";
import {
  MicrosoftAccountSection,
  ProviderLogo,
} from "./-ui.microsoft-account-section";
import {
  type DriveProvider,
  type PipesProvider,
  PROVIDER_LABELS,
  providerNotConfiguredMessage,
  useProviderAccountStatuses,
} from "./-microsoft-providers";
import {
  XeroImportDialog,
  type XeroTenantBinding,
} from "./-ui.xero-reports-card";

type ConnectionWithRun = FunctionReturnType<
  typeof api.integrations.workOsSync.queries.getJobIntegrations
>[number];

type BrowseError =
  "needs_connection" | "provider_not_configured" | "graph_error";

export function JobIntegrationsPanel({ jobId }: { jobId: Id<"jobs"> }) {
  const { data: connections, isLoading } = useQuery(
    convexQuery(api.integrations.workOsSync.queries.getJobIntegrations, {
      jobId,
    }),
  );

  const accountStatuses = useProviderAccountStatuses();

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const byProvider = new Map(
    (connections ?? [])
      .filter((c) => c.status !== "disconnected")
      .map((c) => [c.provider, c]),
  );

  const xeroConnection = byProvider.get("xero");
  const xeroTenant: XeroTenantBinding | null =
    xeroConnection?.xeroTenantId && xeroConnection.xeroTenantName
      ? {
          tenantId: xeroConnection.xeroTenantId,
          tenantName: xeroConnection.xeroTenantName,
        }
      : null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold">Integrations</h2>
        <p className="text-sm text-muted-foreground">
          Connect accounts to import data into this Job. When accounts are
          connected, you will need to set up a sync for the job.
        </p>
      </div>

      <MicrosoftAccountSection
        statuses={accountStatuses.data}
        statusesLoading={accountStatuses.isLoading}
        onChanged={() => void accountStatuses.refetch()}
        renderConnectedAction={(provider) => (
          <JobProviderSyncAction
            jobId={jobId}
            provider={provider}
            connection={
              provider === "xero" ? null : (byProvider.get(provider) ?? null)
            }
            xeroTenant={provider === "xero" ? xeroTenant : null}
          />
        )}
        renderConnectedDetails={(provider) => (
          <JobProviderSyncDetails
            provider={provider}
            connection={
              provider === "xero" ? null : (byProvider.get(provider) ?? null)
            }
            xeroTenant={provider === "xero" ? xeroTenant : null}
          />
        )}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline sync action (rendered next to Disconnect in the connected accounts
// card): "Set up sync" until a sync exists, then "Sync now"; "Import reports"
// for Xero. The sync's folder/run details live in JobProviderSyncDetails below.
// ---------------------------------------------------------------------------

function JobProviderSyncAction({
  jobId,
  provider,
  connection,
  xeroTenant,
}: {
  jobId: Id<"jobs">;
  provider: PipesProvider;
  connection: ConnectionWithRun | null;
  xeroTenant: XeroTenantBinding | null;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const syncNow = useMutation({
    mutationFn: useConvexMutation(
      api.integrations.workOsSync.mutations.startManualSync,
    ),
    onSuccess: () => toast.success("Sync started"),
    onError: (error) => toast.error(error.message),
  });

  if (provider === "xero") {
    return (
      <>
        <Button size="sm" variant="outline" onClick={() => setDialogOpen(true)}>
          {xeroTenant ? "Import reports" : "Set up reports"}
        </Button>
        {dialogOpen && (
          <XeroImportDialog
            jobId={jobId}
            currentTenant={xeroTenant}
            onClose={() => setDialogOpen(false)}
          />
        )}
      </>
    );
  }

  // OneDrive / SharePoint: trigger a manual run once a sync exists, otherwise
  // open the setup dialog.
  if (connection) {
    const isRunning = connection.latestRun?.status === "running";
    return (
      <Button
        size="sm"
        variant="outline"
        disabled={syncNow.isPending || isRunning}
        onClick={() => syncNow.mutate({ connectionId: connection._id })}
      >
        <RefreshCw className="mr-1 h-3.5 w-3.5" />
        {isRunning ? "Syncing…" : "Refresh sync"}
      </Button>
    );
  }

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setDialogOpen(true)}>
        Set up sync
      </Button>
      {dialogOpen && (
        <SetupSyncDialog
          jobId={jobId}
          provider={provider as DriveProvider}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Inline sync details (rendered below the connected account row once a sync /
// Xero binding exists) — folder, sync mode, last-run stats, and remove action.
// ---------------------------------------------------------------------------

function JobProviderSyncDetails({
  provider,
  connection,
  xeroTenant,
}: {
  provider: PipesProvider;
  connection: ConnectionWithRun | null;
  xeroTenant: XeroTenantBinding | null;
}) {
  const removeSync = useMutation({
    mutationFn: useConvexMutation(
      api.integrations.workOsSync.mutations.disconnectConnection,
    ),
    onSuccess: () =>
      toast.success(`${PROVIDER_LABELS[provider]} sync removed from this job`),
    onError: (error) => toast.error(error.message),
  });

  if (provider === "xero") {
    if (!xeroTenant) return null;
    return (
      <div className="mt-3 space-y-1.5 border-t pt-3 text-sm text-muted-foreground">
        <p className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs">Importing from:</span>
          <Badge color="gray" variant="soft" size="sm">
            {xeroTenant.tenantName}
          </Badge>
        </p>
        <p className="text-xs">
          Files are automatically imported as structured documents. Note that
          reimporting a report will replace the prior version on Morgan. You can
          switch this job to a different Xero organisation from the Import
          reports dialog.
        </p>
      </div>
    );
  }

  if (!connection) return null;

  return (
    <div className="mt-3 space-y-2 border-t pt-3 text-sm text-muted-foreground">
      <div className="flex items-start justify-between gap-3">
        <p className="flex min-w-0 flex-wrap items-center gap-1.5">
          <FolderOpen className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 break-all">
            {connection.siteName ? `${connection.siteName} / ` : ""}
            {connection.driveName} / {connection.folderPath || "(root)"}
          </span>
          <Badge
            color={connection.syncMode === "active_sync" ? "blue" : "gray"}
            variant="soft"
            size="sm"
            className="ml-1"
          >
            {connection.syncMode === "active_sync"
              ? "Active sync"
              : "One-off import"}
          </Badge>
          {connection.status === "needs_reauthorization" && (
            <Badge color="amber" variant="soft" size="sm">
              <AlertTriangle className="h-3 w-3" /> Reauthorization required
            </Badge>
          )}
        </p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 shrink-0 px-2 text-destructive hover:text-destructive"
              disabled={removeSync.isPending}
            >
              <Unplug className="mr-1 h-3.5 w-3.5" /> Remove sync
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Remove {PROVIDER_LABELS[provider]} sync from this job?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This stops syncing this folder to the job. Documents already
                imported stay in the job — no files are deleted from the job or
                the drive. You can set the sync up again at any time.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() =>
                  removeSync.mutate({ connectionId: connection._id })
                }
              >
                Remove sync
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      {connection.latestRun && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs">Last run:</span>
          <RunStatusBadge status={connection.latestRun.status} />
          <RunStatBadge
            count={connection.latestRun.stats.imported}
            label="imported"
            activeColor="green"
          />
          <RunStatBadge
            count={connection.latestRun.stats.skippedDuplicates}
            label="duplicates skipped"
            activeColor="amber"
          />
          <RunStatBadge
            count={connection.latestRun.stats.skippedUnchanged}
            label="unchanged"
            activeColor="gray"
          />
          {connection.reverseSyncEnabled && (
            <RunStatBadge
              count={connection.latestRun.stats.exported}
              label="exported"
              activeColor="blue"
            />
          )}
          <RunStatBadge
            count={connection.latestRun.stats.failed}
            label="failed"
            activeColor="red"
          />
        </div>
      )}
      {connection.lastSyncError && (
        <p className="text-destructive">{connection.lastSyncError}</p>
      )}
    </div>
  );
}

function RunStatusBadge({
  status,
}: {
  status: "running" | "completed" | "failed";
}) {
  if (status === "running") {
    return (
      <Badge color="blue" variant="soft" size="sm">
        <Loader2 className="h-3 w-3 animate-spin" /> Running
      </Badge>
    );
  }
  if (status === "failed") {
    return (
      <Badge color="red" variant="soft" size="sm">
        Failed
      </Badge>
    );
  }
  return (
    <Badge color="green" variant="soft" size="sm">
      Completed
    </Badge>
  );
}

/** Stat pill: muted outline when zero so non-zero counts stand out. */
function RunStatBadge({
  count,
  label,
  activeColor,
}: {
  count: number;
  label: string;
  activeColor: BadgeColor;
}) {
  return (
    <Badge
      color={count > 0 ? activeColor : "gray"}
      variant={count > 0 ? "soft" : "outline"}
      size="sm"
      className={count > 0 ? undefined : "text-muted-foreground"}
    >
      {count} {label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Set up sync dialog: pick folder → configure (account already connected)
// ---------------------------------------------------------------------------

/**
 * Mutually-exclusive sync behaviours offered after a folder is picked. Mapped
 * to the backend's (syncMode, reverseSyncEnabled) pair on submit.
 */
type SyncOption = "two_way" | "from_drive" | "one_off";

const SYNC_OPTIONS: Array<{
  value: SyncOption;
  label: string;
  description: string;
}> = [
  {
    value: "from_drive",
    label: "Sync from drive",
    description:
      "New files and updates in the drive are added to Morgan. Files added to Morgan are not synced to the drive.",
  },
  {
    value: "two_way",
    label: "Two-way sync",
    description:
      "New files and updates in the drive are added to Morgan. Files added to Morgan are automatically added to a “Morgan” folder in the drive.",
  },
  {
    value: "one_off",
    label: "One-off import",
    description: "Files are imported into Morgan once without ongoing syncing.",
  },
];

function SetupSyncDialog({
  jobId,
  provider,
  onClose,
}: {
  jobId: Id<"jobs">;
  provider: DriveProvider;
  onClose: () => void;
}) {
  const [step, setStep] = useState<"folder" | "configure">("folder");
  const [site, setSite] = useState<{ id: string; displayName: string } | null>(
    null,
  );
  const [drive, setDrive] = useState<{ id: string; name: string } | null>(null);
  const [folder, setFolder] = useState<{ id: string; path: string }>({
    id: "root",
    path: "",
  });
  const [syncOption, setSyncOption] = useState<SyncOption>("from_drive");

  const createConnection = useMutation({
    mutationFn: useConvexMutation(
      api.integrations.workOsSync.mutations.createDriveConnection,
    ),
    onSuccess: () => {
      toast.success("Sync configured — initial import started");
      onClose();
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            <ProviderLogo provider={provider} className="h-6 w-6" />
            Sync a {PROVIDER_LABELS[provider]} folder to this job
          </DialogTitle>
        </DialogHeader>

        {step === "folder" && (
          <FolderPicker
            provider={provider}
            site={site}
            drive={drive}
            onSiteChange={setSite}
            onDriveChange={setDrive}
            onPick={(picked) => {
              setFolder(picked);
              setStep("configure");
            }}
            onNeedsConnection={() => {
              toast.error(
                `Your ${PROVIDER_LABELS[provider]} account is not connected — connect it from the Integrations tab first.`,
              );
              onClose();
            }}
          />
        )}

        {step === "configure" && drive && (
          <div className="min-w-0 space-y-4">
            <div className="flex items-center gap-2.5 rounded-md border bg-muted/40 px-3 py-2.5">
              <ProviderLogo provider={provider} className="h-5 w-5 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Folder to sync</p>
                <p
                  className="truncate text-sm font-medium"
                  title={`${site ? `${site.displayName} / ` : ""}${drive.name} / ${folder.path || "(root)"}`}
                >
                  {site ? `${site.displayName} / ` : ""}
                  {drive.name} / {folder.path || "(root)"}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Sync settings</p>
              {SYNC_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors hover:bg-muted/40"
                >
                  <Checkbox
                    className="mt-0.5"
                    checked={syncOption === option.value}
                    onCheckedChange={(checked) => {
                      if (checked) setSyncOption(option.value);
                    }}
                  />
                  <span className="space-y-0.5">
                    <span className="block text-sm font-medium">
                      {option.label}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {option.description}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("folder")}>
                Back
              </Button>
              <Button
                disabled={createConnection.isPending}
                onClick={() =>
                  createConnection.mutate({
                    jobId,
                    provider,
                    siteId: site?.id,
                    siteName: site?.displayName,
                    driveId: drive.id,
                    driveName: drive.name,
                    folderId: folder.id,
                    folderPath: folder.path,
                    syncMode:
                      syncOption === "one_off" ? "one_off" : "active_sync",
                    reverseSyncEnabled: syncOption === "two_way",
                  })
                }
              >
                {createConnection.isPending && (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                )}
                Start sync
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Folder picker (site → library → folder breadcrumbs)
// ---------------------------------------------------------------------------

function ListSpinner() {
  return (
    <div className="flex h-28 items-center justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>
  );
}

function FolderPicker({
  provider,
  site,
  drive,
  onSiteChange,
  onDriveChange,
  onPick,
  onNeedsConnection,
}: {
  provider: DriveProvider;
  site: { id: string; displayName: string } | null;
  drive: { id: string; name: string } | null;
  onSiteChange: (site: { id: string; displayName: string } | null) => void;
  onDriveChange: (drive: { id: string; name: string } | null) => void;
  onPick: (folder: { id: string; path: string }) => void;
  onNeedsConnection: () => void;
}) {
  const [siteSearch, setSiteSearch] = useState("");
  const [siteUrl, setSiteUrl] = useState("");
  const [sites, setSites] = useState<
    Array<{ id: string; displayName: string }>
  >([]);
  const [searched, setSearched] = useState(false);
  const [drives, setDrives] = useState<Array<{ id: string; name: string }>>([]);
  const [crumbs, setCrumbs] = useState<Array<{ id: string; name: string }>>([]);
  const [folders, setFolders] = useState<Array<{ id: string; name: string }>>(
    [],
  );
  const [loading, setLoading] = useState(false);

  const browseSites = useAction(
    api.integrations.workOsSync.browseActions.browseSites,
  );
  const resolveSiteByUrl = useAction(
    api.integrations.workOsSync.browseActions.resolveSiteByUrl,
  );
  const browseSiteDrives = useAction(
    api.integrations.workOsSync.browseActions.browseSiteDrives,
  );
  const getMyOneDrive = useAction(
    api.integrations.workOsSync.browseActions.getMyOneDrive,
  );
  const browseFolders = useAction(
    api.integrations.workOsSync.browseActions.browseFolders,
  );

  const currentFolderId =
    crumbs.length > 0 ? crumbs[crumbs.length - 1].id : "root";
  const currentPath = crumbs.map((c) => c.name).join(" / ");

  function handleBrowseFailure(error: BrowseError, message?: string) {
    if (error === "needs_connection") {
      onNeedsConnection();
    } else if (error === "provider_not_configured") {
      toast.error(providerNotConfiguredMessage(provider));
    } else {
      toast.error(message ?? "Something went wrong talking to Microsoft");
    }
  }

  async function loadFolders(driveId: string, folderId: string) {
    setLoading(true);
    try {
      const result = await browseFolders({ provider, driveId, folderId });
      if (!result.ok) {
        handleBrowseFailure(result.error, result.message);
        return;
      }
      setFolders(result.data.map((f) => ({ id: f.id, name: f.name })));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to load folders",
      );
    } finally {
      setLoading(false);
    }
  }

  async function runSiteSearch() {
    setLoading(true);
    try {
      const result = await browseSites({ search: siteSearch });
      if (!result.ok) {
        handleBrowseFailure(result.error, result.message);
        return;
      }
      setSites(result.data);
      setSearched(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Search failed");
    } finally {
      setLoading(false);
    }
  }

  async function selectSite(picked: { id: string; displayName: string }) {
    onSiteChange(picked);
    setLoading(true);
    try {
      const result = await browseSiteDrives({ siteId: picked.id });
      if (!result.ok) {
        handleBrowseFailure(result.error, result.message);
        return;
      }
      setDrives(result.data);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to load libraries",
      );
    } finally {
      setLoading(false);
    }
  }

  // OneDrive has a single implicit drive: resolve it on mount.
  useEffect(() => {
    if (provider !== "microsoft-onedrive" || drive) return;
    setLoading(true);
    void getMyOneDrive({})
      .then((result) => {
        if (!result.ok) {
          handleBrowseFailure(result.error, result.message);
          return;
        }
        onDriveChange(result.data);
        void loadFolders(result.data.id, "root");
      })
      .catch((error: unknown) => {
        toast.error(
          error instanceof Error ? error.message : "Failed to load OneDrive",
        );
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot resolve per provider/drive: the helpers (loadFolders, handleBrowseFailure, onDriveChange) get new identities each render, and re-running while `drive` is still unset would kick off duplicate OneDrive fetches mid-flight
  }, [provider, drive]);

  // SharePoint: site search step
  if (provider === "microsoft-sharepoint" && !site) {
    return (
      <div className="min-w-0 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Search SharePoint sites…"
              value={siteSearch}
              onChange={(event) => setSiteSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void runSiteSearch();
                }
              }}
            />
          </div>
          <Button
            variant="outline"
            disabled={loading}
            onClick={() => void runSiteSearch()}
          >
            Search
          </Button>
        </div>

        <div className="max-h-56 overflow-y-auto rounded-md border">
          {loading ? (
            <ListSpinner />
          ) : sites.length === 0 ? (
            <div className="flex flex-col items-center gap-1.5 px-4 py-8 text-center">
              <Globe className="h-7 w-7 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">
                {searched
                  ? "No sites matched your search."
                  : "Search for a SharePoint site, or paste a link below."}
              </p>
              <p className="text-xs text-muted-foreground/70">
                SharePoint requires a work or school Microsoft account.
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {sites.map((siteOption) => (
                <button
                  key={siteOption.id}
                  type="button"
                  className="group flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted/60"
                  onClick={() => void selectSite(siteOption)}
                >
                  <Globe className="h-4 w-4 shrink-0 text-sky-600" />
                  <span className="truncate" title={siteOption.displayName}>
                    {siteOption.displayName}
                  </span>
                  <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground">
            or paste a site link
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <div className="flex gap-2">
          <Input
            placeholder="https://yourcompany.sharepoint.com/sites/…"
            value={siteUrl}
            onChange={(event) => setSiteUrl(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") event.preventDefault();
            }}
          />
          <Button
            variant="outline"
            disabled={loading || siteUrl.trim() === ""}
            onClick={async () => {
              setLoading(true);
              try {
                const result = await resolveSiteByUrl({
                  url: siteUrl.trim(),
                });
                if (!result.ok) {
                  handleBrowseFailure(result.error, result.message);
                  return;
                }
                await selectSite({
                  id: result.data.id,
                  displayName: result.data.displayName,
                });
              } catch (error) {
                toast.error(
                  error instanceof Error
                    ? error.message
                    : "Could not resolve that URL",
                );
              } finally {
                setLoading(false);
              }
            }}
          >
            Open
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          In Teams: Files tab → “Open in SharePoint”, then copy the address.
        </p>
      </div>
    );
  }

  // SharePoint: document library step
  if (provider === "microsoft-sharepoint" && site && !drive) {
    return (
      <div className="min-w-0 space-y-3">
        <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2">
          <div className="flex min-w-0 items-center gap-2 text-sm">
            <Globe className="h-4 w-4 shrink-0 text-sky-600" />
            <span className="truncate font-medium" title={site.displayName}>
              {site.displayName}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={() => {
              onSiteChange(null);
              setDrives([]);
            }}
          >
            Change site
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Choose a document library:
        </p>
        <div className="max-h-64 overflow-y-auto rounded-md border">
          {loading ? (
            <ListSpinner />
          ) : drives.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              No document libraries found in this site.
            </p>
          ) : (
            <div className="divide-y">
              {drives.map((driveOption) => (
                <button
                  key={driveOption.id}
                  type="button"
                  className="group flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted/60"
                  onClick={() => {
                    onDriveChange(driveOption);
                    void loadFolders(driveOption.id, "root");
                  }}
                >
                  <Library className="h-4 w-4 shrink-0 text-teal-600" />
                  <span className="truncate" title={driveOption.name}>
                    {driveOption.name}
                  </span>
                  <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Folder browsing step (both providers)
  return (
    <div className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-center gap-0.5 rounded-md border bg-muted/40 px-2 py-1.5 text-sm">
        <button
          type="button"
          className={`flex min-w-0 items-center gap-1.5 rounded px-1.5 py-0.5 transition-colors hover:bg-muted ${
            crumbs.length === 0
              ? "font-medium text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
          title={drive?.name ?? "Drive"}
          onClick={() => {
            setCrumbs([]);
            if (drive) void loadFolders(drive.id, "root");
          }}
        >
          <ProviderLogo provider={provider} className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{drive?.name ?? "Drive"}</span>
        </button>
        {crumbs.map((crumb, index) => (
          <span key={crumb.id} className="flex min-w-0 items-center gap-0.5">
            <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground/60" />
            <button
              type="button"
              className={`min-w-0 truncate rounded px-1.5 py-0.5 transition-colors hover:bg-muted ${
                index === crumbs.length - 1
                  ? "font-medium text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              title={crumb.name}
              onClick={() => {
                setCrumbs(crumbs.slice(0, index + 1));
                if (drive) void loadFolders(drive.id, crumb.id);
              }}
            >
              {crumb.name}
            </button>
          </span>
        ))}
      </div>

      <div className="max-h-72 overflow-y-auto rounded-md border">
        {loading ? (
          <ListSpinner />
        ) : folders.length === 0 ? (
          <div className="flex flex-col items-center gap-1.5 px-4 py-8 text-center">
            <FolderOpen className="h-7 w-7 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No subfolders</p>
            <p className="text-xs text-muted-foreground/70">
              You can sync this folder directly.
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {folders.map((folderOption) => (
              <button
                key={folderOption.id}
                type="button"
                className="group flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted/60"
                onClick={() => {
                  setCrumbs([...crumbs, folderOption]);
                  if (drive) void loadFolders(drive.id, folderOption.id);
                }}
              >
                <Folder className="h-4 w-4 shrink-0 text-sky-500" />
                <span className="truncate" title={folderOption.name}>
                  {folderOption.name}
                </span>
                <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </button>
            ))}
          </div>
        )}
      </div>

      <DialogFooter className="items-center gap-3 sm:justify-between">
        <p
          className="min-w-0 flex-1 truncate text-xs text-muted-foreground"
          title={`${drive?.name ?? "Drive"}${currentPath ? ` / ${currentPath}` : " (root)"}`}
        >
          Selected:{" "}
          <span className="font-medium text-foreground">
            {drive?.name ?? "Drive"}
            {currentPath ? ` / ${currentPath}` : " (root)"}
          </span>
        </p>
        <Button
          disabled={loading || !drive}
          onClick={() => onPick({ id: currentFolderId, path: currentPath })}
        >
          Sync this folder
        </Button>
      </DialogFooter>
    </div>
  );
}
